/**
 * DashboardDetailModal.js
 * PC 대시보드 상세 정보 모달 (Dialog 기반)
 * 모바일과 동일한 상세 정보를 Dialog로 표시
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Paper,
  Chip,
  Divider,
  Button,
} from '@mui/material';
import { X } from 'react-feather';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { format, eachDayOfInterval, parseISO } from 'date-fns';
import { ko } from 'date-fns/locale';
import apiConfig from '../config/api';

const API_BASE_URL = apiConfig.V1;

const DashboardDetailModal = ({
  open,
  onClose,
  type, // 'today', 'week', 'projects', 'delays'
  dateRange,
  selectedEmployees = [], // 선택된 담당자 목록
}) => {
  const { colors, isDark } = useCustomTheme();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [contracts, setContracts] = useState([]);

  // 업체 및 계약 목록 조회
  useEffect(() => {
    const fetchReferences = async () => {
      try {
        const [companiesRes, contractsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/admin/customers`).catch(() => ({ data: [] })),
          axios.get(`${API_BASE_URL}/contracts/recent`, { params: { limit: 20 } }).catch(() => ({ data: [] })),
        ]);
        setCompanies(companiesRes.data || []);
        setContracts(contractsRes.data || []);
      } catch (error) {
        console.error('기준정보 조회 실패:', error);
      }
    };
    fetchReferences();
  }, []);

  // 상세 데이터 조회
  useEffect(() => {
    if (open && type) {
      fetchDetailData();
    }
  }, [open, type, dateRange, selectedEmployees]);

  const fetchDetailData = async () => {
    setLoading(true);
    try {
      // dateRange가 없으면 기본값 사용
      const startDate = dateRange?.fromDate || new Date().toISOString().split('T')[0];
      const endDate = dateRange?.toDate || new Date().toISOString().split('T')[0];
      
      switch (type) {
        case 'today': {
          // 선택된 기간 내 투입시간 내역
          // 담당자가 선택된 경우: 선택된 담당자의 투입시간만 조회
          // 담당자 선택 없음: 접근 가능한 모든 직원 자동 집계
          const todayParams = {
            start_date: startDate,
            end_date: endDate
          };
          
          // 선택된 담당자가 있으면 employee_codes 파라미터 추가
          if (Array.isArray(selectedEmployees) && selectedEmployees.length > 0) {
            todayParams.employee_codes = selectedEmployees.join(',');
          }
          
          const response = await axios.get(`${API_BASE_URL}/dashboard/timelog-details`, {
            params: todayParams
          });
          setData(response.data || []);
          break;
        }
        case 'week': {
          // 최근 7일간 일별 누적 시간 리스트
          // 오늘부터 7일 전까지의 각 날짜별 총 투입시간 계산
          const today = new Date();
          const sevenDaysAgo = new Date(today);
          sevenDaysAgo.setDate(today.getDate() - 6); // 오늘 포함 7일
          
          const weekStartDate = format(sevenDaysAgo, 'yyyy-MM-dd');
          const weekEndDate = format(today, 'yyyy-MM-dd');
          
          // 담당자가 선택된 경우: 선택된 담당자의 투입시간만 조회
          // 담당자 선택 없음: 접근 가능한 모든 직원 자동 집계
          const weekParams = {
            start_date: weekStartDate,
            end_date: weekEndDate
          };
          
          // 선택된 담당자가 있으면 employee_codes 파라미터 추가
          if (Array.isArray(selectedEmployees) && selectedEmployees.length > 0) {
            weekParams.employee_codes = selectedEmployees.join(',');
          }
          
          try {
            const response = await axios.get(`${API_BASE_URL}/dashboard/timelog-details`, {
              params: weekParams
            });
            const allLogs = response.data || [];
            
            // 날짜별로 그룹화하여 집계
            const days = eachDayOfInterval({ 
              start: sevenDaysAgo, 
              end: today 
            });
            
            const daySummaries = days.map((day) => {
              const dayStr = format(day, 'yyyy-MM-dd');
              const dayLogs = allLogs.filter(log => log.log_date === dayStr);
              const totalMinutes = dayLogs.reduce((sum, log) => sum + (log.input_minute || 0), 0);
              
              return {
                date: dayStr,
                dateObj: day,
                total_minutes: totalMinutes,
                log_count: dayLogs.length,
              };
            });
            
            // 날짜 역순 정렬 (최신 날짜가 위로)
            daySummaries.sort((a, b) => b.date.localeCompare(a.date));
            setData(daySummaries);
          } catch (error) {
            console.error('최근 7일 데이터 조회 실패:', error);
            setData([]);
          }
          break;
        }
        case 'projects': {
          // 프로젝트 리스트 (기간 내 투입시간이 있는 프로젝트)
          // dateRange에서 year, month 추출
          const startDateObj = parseISO(startDate);
          const endDateObj = parseISO(endDate);
          const startYear = startDateObj.getFullYear();
          const startMonth = startDateObj.getMonth() + 1;
          const endYear = endDateObj.getFullYear();
          const endMonth = endDateObj.getMonth() + 1;
          
          // 기간 내 모든 프로젝트 조회
          // 담당자가 선택된 경우: 선택된 담당자의 프로젝트만 조회
          // 담당자 선택 없음: 접근 가능한 모든 직원 자동 집계
          const projectParams = {
            start_date: startDate,
            end_date: endDate,
            year: startYear,
            month: startMonth,
          };
          
          // 종료일이 다른 월이면 추가 파라미터 필요할 수 있음
          if (endYear !== startYear || endMonth !== startMonth) {
            projectParams.end_year = endYear;
            projectParams.end_month = endMonth;
          }
          
          // 선택된 담당자가 있으면 employee_codes 파라미터 추가
          if (Array.isArray(selectedEmployees) && selectedEmployees.length > 0) {
            projectParams.employee_codes = selectedEmployees.join(',');
          }
          
          const response = await axios.get(`${API_BASE_URL}/dashboard/project-hours`, {
            params: projectParams
          }).catch(() => ({ data: [] }));
          
          // 프로젝트 리스트 데이터 가공 (중복 제거: 같은 contract_id는 한 번만)
          const projectMap = new Map();
          (response.data || []).forEach(project => {
            const projectKey = project.contract_id;
            if (!projectMap.has(projectKey)) {
              projectMap.set(projectKey, {
                contract_id: project.contract_id,
                contract_title: project.contract_title,
                company_code: project.company_code,
                company_name_kr: project.company_name_kr,
                total_minutes: project.total_minutes || 0,
              });
            } else {
              // 이미 존재하는 프로젝트인 경우, total_minutes 합산
              const existing = projectMap.get(projectKey);
              existing.total_minutes = (existing.total_minutes || 0) + (project.total_minutes || 0);
            }
          });
          
          // Map을 배열로 변환
          const projectList = Array.from(projectMap.values());
          setData(projectList);
          break;
        }
        case 'delays': {
          // 선택된 기간 내 지연 입력 내역
          // 시작일과 종료일 모두 포함
          const startDateObj = parseISO(startDate);
          const endDateObj = parseISO(endDate);
          const days = eachDayOfInterval({ start: startDateObj, end: endDateObj });
          
          // 담당자가 선택된 경우: 선택된 담당자의 투입시간만 조회
          // 담당자 선택 없음: 접근 가능한 모든 직원 자동 집계
          const delaysParams = {
            start_date: startDate,
            end_date: endDate
          };
          
          // 선택된 담당자가 있으면 employee_codes 파라미터 추가
          if (Array.isArray(selectedEmployees) && selectedEmployees.length > 0) {
            delaysParams.employee_codes = selectedEmployees.join(',');
          }
          
          try {
            const response = await axios.get(`${API_BASE_URL}/dashboard/timelog-details`, {
              params: delaysParams
            });
            const allLogs = response.data || [];
            
            // 날짜별로 투입시간이 있는지 확인
            const loggedDates = new Set();
            allLogs.forEach(log => {
              if (log.log_date && (log.input_minute || 0) > 0) {
                loggedDates.add(log.log_date);
              }
            });
            
            // 투입시간이 없는 날짜만 수집
            const delayDays = [];
            for (const day of days) {
              const dayStr = format(day, 'yyyy-MM-dd');
              const dateObj = new Date(day.getTime());
              
              if (!loggedDates.has(dayStr)) {
                delayDays.push({
                  date: dayStr,
                  dateObj: dateObj,
                });
              }
            }
            
            setData(delayDays);
          } catch (error) {
            console.error('입력지연 데이터 조회 실패:', error);
            setData([]);
          }
          break;
        }
        default:
          setData([]);
      }
    } catch (error) {
      console.error('상세 데이터 조회 실패:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'today':
        return '오늘 업무시간 내역';
      case 'week':
        return '최근 7일 누적';
      case 'projects':
        return '프로젝트 건수';
      case 'delays':
        return '입력지연 내역';
      default:
        return '상세 정보';
    }
  };

  // 업체명칭 찾기
  const getCompanyName = (companyCode) => {
    if (!companyCode) return '';
    const company = companies.find(c => c.company_code === companyCode);
    return company?.company_name_kr || companyCode;
  };

  // 계약명칭 찾기
  const getContractTitle = (contractId) => {
    if (!contractId) return '';
    const contract = contracts.find(c => c.contract_id === contractId);
    return contract?.contract_title || contractId;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: isDark ? '#1C1D22' : '#FFFFFF',
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${colors.primaryBorder}`,
          pb: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: colors.primaryText,
            fontSize: '18px',
          }}
        >
          {getTitle()}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: colors.secondaryText,
            '&:hover': {
              backgroundColor: isDark ? '#2A2B30' : '#F5F5F5',
            },
          }}
        >
          <X size={24} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ 
        p: 3, 
        maxHeight: '70vh', 
        overflow: 'auto',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch', // iOS 부드러운 스크롤
        touchAction: 'pan-y', // 수직 스크롤만 허용
      }}>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" sx={{ color: colors.secondaryText }}>
              로딩 중...
            </Typography>
          </Box>
        ) : data.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="body1" sx={{ color: colors.secondaryText, mb: 1 }}>
              데이터가 없습니다
            </Typography>
          </Box>
        ) : (
          <Box>
            {type === 'today' && data.map((log, index) => (
              <React.Fragment key={log.timelog_id || index}>
                <Paper
                  sx={{
                    p: 2.5,
                    mb: 2,
                    backgroundColor: isDark ? '#25262B' : '#F8F9FA',
                    border: `1px solid ${colors.primaryBorder}`,
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          color: colors.primaryText,
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        {getCompanyName(log.company_code)} - {getContractTitle(log.contract_id)}
                      </Typography>
                      {log.input_description && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: colors.secondaryText,
                            lineHeight: 1.5,
                          }}
                        >
                          {log.input_description}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={`${Math.round((log.input_minute || 0) / 60 * 10) / 10}h`}
                      size="small"
                      sx={{
                        backgroundColor: colors.accent,
                        color: '#FFFFFF',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {log.job_category && (
                      <Chip
                        label={log.job_category}
                        size="small"
                      />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.secondaryText,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {log.log_date ? format(new Date(log.log_date), 'yyyy-MM-dd', { locale: ko }) : ''}
                    </Typography>
                  </Box>
                </Paper>
                {index < data.length - 1 && <Divider sx={{ my: 1 }} />}
              </React.Fragment>
            ))}

            {type === 'week' && data.map((daySummary, index) => {
              const dayOfWeek = daySummary.dateObj ? format(daySummary.dateObj, 'EEE', { locale: ko }) : '';
              const dateFormatted = daySummary.dateObj 
                ? format(daySummary.dateObj, 'yyyy년 MM월 dd일', { locale: ko })
                : daySummary.date;
              
              return (
              <React.Fragment key={daySummary.date || `week-${index}`}>
                <Paper
                  sx={{
                    p: 2.5,
                    mb: 2,
                    backgroundColor: isDark ? '#25262B' : '#F8F9FA',
                    border: `1px solid ${colors.primaryBorder}`,
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          color: colors.primaryText,
                          fontWeight: 600,
                          mb: 0.5,
                        }}
                      >
                        {dateFormatted} ({dayOfWeek})
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.secondaryText,
                        }}
                      >
                        입력 건수: {daySummary.log_count || 0}건
                      </Typography>
                    </Box>
                    <Chip
                      label={`${Math.round((daySummary.total_minutes || 0) / 60 * 10) / 10}시간`}
                      size="small"
                      sx={{
                        backgroundColor: colors.accent,
                        color: '#FFFFFF',
                        fontWeight: 600,
                        fontSize: '14px',
                      }}
                    />
                  </Box>
                </Paper>
                {index < data.length - 1 && <Divider sx={{ my: 1 }} />}
              </React.Fragment>
              );
            })}

            {type === 'projects' && data.map((project, index) => (
              <React.Fragment key={project.contract_id || `project-${index}`}>
                <Paper
                  sx={{
                    p: 2.5,
                    mb: 2,
                    backgroundColor: isDark ? '#25262B' : '#F8F9FA',
                    border: `1px solid ${colors.primaryBorder}`,
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          color: colors.primaryText,
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        {getCompanyName(project.company_code) || project.company_name_kr || project.company_code || '미지정 업체'} - {getContractTitle(project.contract_id) || project.contract_title || project.contract_id || '미지정 프로젝트'}
                      </Typography>
                    </Box>
                    <Chip
                      label={`${Math.round((project.total_minutes || 0) / 60 * 10) / 10}h`}
                      size="small"
                      sx={{
                        backgroundColor: colors.accent,
                        color: '#FFFFFF',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {project.company_code && (
                      <Chip
                        label={`업체: ${getCompanyName(project.company_code) || project.company_name_kr || project.company_code}`}
                        size="small"
                      />
                    )}
                    {project.contract_id && (
                      <Chip
                        label={`프로젝트: ${getContractTitle(project.contract_id) || project.contract_title || project.contract_id}`}
                        size="small"
                      />
                    )}
                  </Box>
                </Paper>
                {index < data.length - 1 && <Divider sx={{ my: 1 }} />}
              </React.Fragment>
            ))}

            {type === 'delays' && data.map((delay, index) => {
              // 날짜 객체 유효성 검사
              let dateToFormat = null;
              if (delay.dateObj instanceof Date && !isNaN(delay.dateObj.getTime())) {
                dateToFormat = delay.dateObj;
              } else if (delay.date) {
                dateToFormat = new Date(delay.date);
                if (isNaN(dateToFormat.getTime())) {
                  dateToFormat = null;
                }
              }
              
              return (
              <React.Fragment key={delay.date || `delay-${index}`}>
                <Paper
                  sx={{
                    p: 2.5,
                    mb: 2,
                    backgroundColor: isDark ? '#25262B' : '#F8F9FA',
                    border: `1px solid ${colors.error || '#f44336'}`,
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography
                        variant="body1"
                        sx={{
                          color: colors.primaryText,
                          fontWeight: 600,
                          mb: 0.5,
                        }}
                      >
                        {dateToFormat 
                          ? format(dateToFormat, 'yyyy년 MM월 dd일 (E)', { locale: ko })
                          : delay.date || '날짜 정보 없음'}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.secondaryText,
                        }}
                      >
                        투입시간 미입력
                      </Typography>
                    </Box>
                    <Chip
                      label="지연"
                      size="small"
                      sx={{
                        backgroundColor: colors.error || '#f44336',
                        color: '#FFFFFF',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Paper>
                {index < data.length - 1 && <Divider sx={{ my: 1 }} />}
              </React.Fragment>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: `1px solid ${colors.primaryBorder}` }}>
        <Button onClick={onClose} variant="outlined" sx={{ minWidth: 100 }}>
          닫기
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DashboardDetailModal;

